import axios from 'axios';
import properties from '../../properties';
import {
  RegenerateResponse,
  UploadContractsResponse,
} from '../../types/contracts.types';

export const addHealthCarePartner = async (
  fields: any,
  egNid: string,
  isAIGenerated?: boolean,
  handleOpenSnackBar?: any,
) => {
  try {
    const response = await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.addHealthcarePartner,
      {
        eg_nid: egNid,
        healthCarePartnersFields: fields,
        is_AI_generated: isAIGenerated,
      },
    );

    if (response.status === 200) {
      return response?.data?.data; // Return the response data
    } else {
      throw new Error('Unexpected response status'); // Handle unexpected status codes
    }
  } catch (error: any) {
    // Handle the error
    handleOpenSnackBar(
      error.response?.data?.error || 'An error occurred',
      'error',
      6000,
    );
    return null; // Return null or an appropriate value in case of an error
  }
};

export const getHealthCarePartners = async (
  egNid: string,
  service_agreement_id?: string,
  handleOpenSnackBar?: any,
) => {
  return await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.getHealthcarePartner,
      {
        eg_nid: egNid,
        service_agreement_id: service_agreement_id,
      },
    )
    .then((res: any) => {
      if (res.status === 200) {
        const data = res?.data?.data;
        return data;
      } else {
        handleOpenSnackBar(
          'Failed to fetch healthcare partners',
          'error',
          6000,
        );
        return [];
      }
    })
    .catch((error) => {
      handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      return [];
    });
};

export const getServiceAgreements = async (handleOpenSnackBar?: any) => {
  return await axios
    .get(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.getServiceAgreements,
    )
    .then((res: any) => {
      if (res.status === 200) {
        return res?.data?.data;
      }
    })
    .catch((error) => {
      handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      return [];
    });
};

export const updateContract = async (
  egNid: string,
  contractId: string,
  updateFields: any,
  handleOpenSnackBar?: any,
) => {
  return await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.updateContract,
      {
        eg_nid: egNid,
        contract_id: contractId,
        updateFields,
      },
    )
    .then((response) => {
      if (response.status === 200) {
        handleOpenSnackBar('Contract updated successfully', 'success', 6000);
        return response?.data?.data; // Return the updated contract data
      }
    })
    .catch((error) => {
      handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      return null; // Return null or an appropriate value in case of an error
    });
};

const base = () => properties.aiqBaseUrl + properties.contractV2BaseUrl;

/**
 * Upload contract files. Two-phase backend: Azure upload + DB row first, then
 * async extraction is queued. Returns immediately with per-file results.
 *
 * Accepts 200 (all uploaded) and 207 (partial) as success; anything else throws.
 */
export const uploadContracts = async (
  files: File[],
  egNid: string,
  employer: string,
  extractionType: 'landing_ai' | 'openai' = 'landing_ai',
): Promise<UploadContractsResponse> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('eg_nid', egNid);
  formData.append('employer', employer);
  formData.append('extraction_type', extractionType);

  const res = await axios.post(
    `${base()}${properties.uploadContracts}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: (s) => s === 200 || s === 207,
    },
  );
  return res.data as UploadContractsResponse;
};

/**
 * Poll the review/processing state for a group.
 */
export const getReviewState = async (egNid: string, createdBy?: string) => {
  const res = await axios.get(`${base()}${properties.reviewState}`, {
    params: { eg_nid: egNid, ...(createdBy ? { createdBy } : {}) },
  });
  return res.data;
};

/**
 * Create contracts from reviewed documents (partial commit supported).
 */
export const addContract = async (egNid: string, documents: any[]) => {
  const res = await axios.post(`${base()}${properties.addContract}`, {
    eg_nid: egNid,
    documents,
  });
  return res.data;
};

/**
 * Re-enqueue documents whose extraction failed.
 * @param egNid       group id
 * @param documentId  optional — retry only this doc; omit to retry all failed.
 */
export const retryFailed = async (
  egNid: string,
  documentId?: string,
): Promise<{ retried: number }> => {
  const res = await axios.post(`${base()}${properties.retryFailed}`, {
    eg_nid: egNid,
    ...(documentId ? { document_id: documentId } : {}),
  });
  return res.data;
};

/**
 * Fetch contracts for a group, enriched with service_agreement_name + documents.
 * @param isArchived omit for all, false for active only, true for archived only.
 */
export const getContracts = async (egNid: string, isArchived?: boolean) => {
  const res = await axios.post(`${base()}${properties.getContracts}`, {
    eg_nid: egNid,
    ...(isArchived === undefined ? {} : { is_archived: isArchived }),
  });
  return res.data;
};

/**
 * Regenerate ALL metadata extractions for a contract.
 */
export const regenerateMetadata = async (
  contractId: string,
): Promise<RegenerateResponse> => {
  const res = await axios.post(`${base()}${properties.regenerateMetadata}`, {
    contract_id: contractId,
  });
  return res.data as RegenerateResponse;
};

/**
 * Regenerate ONE report for a contract.
 */
export const regenerateReport = async (
  contractId: string,
  reportName: string,
): Promise<RegenerateResponse> => {
  const res = await axios.post(`${base()}${properties.regenerateReport}`, {
    contract_id: contractId,
    report_name: reportName,
  });
  return res.data as RegenerateResponse;
};
